import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Building2, Mail, User, ArrowLeft, Languages } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSendSignupOTPMutation } from "../../store/api/auth";
import { WORKSPACE_DEFAULT_TIMEZONE } from "../../utils/constants";
import { handleApiError } from "../../utils/apiErrors";

export default function SignUpPage() {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  
  const [sendSignupOTP, { isLoading }] = useSendSignupOTPMutation();
  
  const [workspaceName, setWorkspaceName] = useState("");
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const browserTimeZone = useMemo(() => {
    try {
      return new Intl.DateTimeFormat().resolvedOptions().timeZone || WORKSPACE_DEFAULT_TIMEZONE;
    } catch {
      return WORKSPACE_DEFAULT_TIMEZONE;
    }
  }, []);

  const toggleLanguage = () => {
    setLanguage(language === 'ja' ? 'en' : 'ja');
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!workspaceName || !userName || !email) {
      toast.error(t('auth.allFieldsRequired'));
      return;
    }

    try {
      const resp: any = await sendSignupOTP({ email });
      
      if (resp.data) {
        toast.success(resp.data.message || t('auth.otpSent'));
        
        // Navigate to verify OTP page with state
        navigate('/verify-signup-otp', { 
          state: { 
            email, 
            workspaceName, 
            userName,
            timezone: browserTimeZone,
          } 
        });
      }
      
      if (resp.error) {
        handleApiError(resp.error, t('auth.otpSendFailed'));
      }
    } catch (error: any) {
      console.error('Send signup OTP error:', error);
      handleApiError(error, t('auth.otpSendFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* 言語切り替えボタン */}
      <div className="absolute top-4 right-4">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLanguage}
          className="gap-2"
        >
          <Languages className="w-4 h-4" />
          {language === 'ja' ? t('auth.switchToEnglish') : t('auth.switchToJapanese')}
        </Button>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">{t('auth.signupTitle')}</CardTitle>
          <CardDescription>
            {t('auth.signupDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendOTP} className="space-y-4">
              <div className="space-y-2">
                <Label className="mb-2" htmlFor="workspaceName">{t('auth.workspaceName')}</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="workspaceName"
                    type="text"
                    placeholder={t('auth.workspaceNamePlaceholder')}
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="mb-2" htmlFor="userName">{t('auth.yourName')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="userName"
                    type="text"
                    placeholder={t('auth.yourNamePlaceholder')}
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="mb-2" htmlFor="email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading 
                  ? t('auth.sending')
                  : t('auth.sendOtpButton')
                }
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/login")}
                disabled={isLoading}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('auth.backToLogin')}
              </Button>
            </div>

            <div className="mt-4 text-center text-xs text-gray-500">
              <p>
                {t('auth.workspaceOwnerNote')}
                <br />
                {t('auth.workspaceOwnerNote2')}
              </p>
            </div>
        </CardContent>
      </Card>
    </div>
  );
}

